/**
 * Turns a canvas drawing into an MNIST-compatible 28x28 input.
 *
 * The downsampling deliberately mirrors what MNIST looks like: white digit on a
 * black background, centered by the bounding box of the drawn strokes, because
 * the network was trained on centered digits.
 */
import { MNIST } from '../../shared/mnist/idx.ts';

export interface PreprocessOptions {
  /** Canvas size in pixels (square). */
  size: number;
  /** Pixels of transparent padding kept around the digit. */
  padding?: number;
}

/**
 * Extracts a normalized 28x28 buffer (same normalization as the training data)
 * from a square canvas that contains a white digit on a black background.
 */
export function canvasToInput(
  context: CanvasRenderingContext2D,
  options: PreprocessOptions,
): Float32Array {
  const { size } = options;
  const padding = options.padding ?? 2;

  const { data } = context.getImageData(0, 0, size, size);
  const gray = new Float32Array(MNIST.pixels);
  const scale = MNIST.rows / size;

  // Box-filter downsample: each MNIST pixel averages its source region.
  for (let y = 0; y < MNIST.rows; y += 1) {
    for (let x = 0; x < MNIST.cols; x += 1) {
      const x0 = Math.floor(x / scale);
      const x1 = Math.max(x0 + 1, Math.floor((x + 1) / scale));
      const y0 = Math.floor(y / scale);
      const y1 = Math.max(y0 + 1, Math.floor((y + 1) / scale));
      let sum = 0;
      let samples = 0;

      for (let sy = y0; sy < Math.min(y1, size); sy += 1) {
        for (let sx = x0; sx < Math.min(x1, size); sx += 1) {
          const offset = (sy * size + sx) * 4;
          // Red channel is enough: the canvas is monochrome.
          sum += data[offset] ?? 0;
          samples += 1;
        }
      }

      gray[y * MNIST.cols + x] = samples > 0 ? sum / samples / 255 : 0;
    }
  }

  return normalize(gray, padding);
}

/** Centers the ink by its bounding box and applies MNIST normalization. */
export function normalize(gray: Float32Array, padding: number): Float32Array {
  let minX: number = MNIST.cols;
  let minY: number = MNIST.rows;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < MNIST.rows; y += 1) {
    for (let x = 0; x < MNIST.cols; x += 1) {
      if ((gray[y * MNIST.cols + x] ?? 0) > 0.05) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  const out = new Float32Array(MNIST.pixels);

  // Empty canvas: return a fully normalized "black" image instead of NaN.
  if (maxX < minX || maxY < minY) {
    return out.fill((0 - MNIST.mean) / MNIST.std);
  }

  const width = maxX - minX + 1;
  const height = maxY - minY + 1;
  const extent = Math.max(width, height);
  const target = MNIST.rows - padding * 2;

  for (let ty = 0; ty < target; ty += 1) {
    for (let tx = 0; tx < target; tx += 1) {
      const sx = minX + Math.floor((tx * extent) / target) - Math.floor((extent - width) / 2);
      const sy = minY + Math.floor((ty * extent) / target) - Math.floor((extent - height) / 2);
      if (sx < 0 || sy < 0 || sx >= MNIST.cols || sy >= MNIST.rows) {
        continue;
      }
      const value = gray[sy * MNIST.cols + sx] ?? 0;
      out[(ty + padding) * MNIST.cols + (tx + padding)] = (value - MNIST.mean) / MNIST.std;
    }
  }

  return out;
}

export { MNIST };
